import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { type Account, type AzureStorageAccount } from '@repo/api-gw-sdk';
import { useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';

import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import type { RestoreGenericStorageProps } from './bucketSelection';

import SelectPlaceholder from '../selectPlaceholder';

type StorageAccountSelectionStepProps<T> = StepProps<
  T & {
    restoreAccountId?: string;
  }
>;

export const StorageAccountSelectionStep = <
  T extends RestoreGenericStorageProps,
>(
  props: StorageAccountSelectionStepProps<T> & {
    allowUsingSourceContainer: boolean;
  }
) => {
  const { setWizardState: setStorageAccountSelectionState } = props;
  const dal = useDAL();
  const { data: pref } = dal.preferences.getUserPref(
    PreferencesKey.RestoreStorageAccount
  );

  useEffect(() => {
    if (pref?.value?.accountName) {
      setStorageAccountSelectionState((state) => ({
        ...state,
        StorageAccount: {
          ...state.StorageAccount,
          AccountName: pref.value!.accountName,
          Container: pref.value!.container,
          ResourceGroup: pref.value!.resourceGroup,
        },
      }));
    }
  }, [pref, setStorageAccountSelectionState]);

  return (
    <StorageAccountSelection
      {...props.wizardState}
      allowUsingSourceContainer={props.allowUsingSourceContainer}
      setStorageAccountSelectionState={setStorageAccountSelectionState}
    />
  );
};

interface StorageAccountProps<T> extends RestoreGenericStorageProps {
  restoreAccount?: Account;
  allowUsingSourceContainer: boolean;
  restrictToRegion?: string;
  setStorageAccountSelectionState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: Account;
      }
    >
  >;
}

export const StorageAccountSelection = <T extends RestoreGenericStorageProps>(
  props: StorageAccountProps<T>
) => {
  const {
    restoreAccount,
    prefix,
    StorageAccount,
    setStorageAccountSelectionState,
    restrictToRegion,
  } = props;
  const dal = useDAL();
  const { data: storageAccountsResponse } = dal.restore.storageAccounts.list(
    restoreAccount?.id ?? ''
  );
  const storageAccounts = (
    storageAccountsResponse?.storageAccounts ?? []
  ).filter((x) => !restrictToRegion || x.location === restrictToRegion);

  const selectedStorageAccount = storageAccounts.find(
    (x) => x.name === StorageAccount.AccountName
  );
  const { data: containersResponse } =
    dal.restore.storageAccounts.listContainers(
      restoreAccount?.id ?? '',
      selectedStorageAccount?.name ?? '',
      selectedStorageAccount?.resourceGroup ?? ''
    );

  const containersList = useMemo(() => {
    const list = (containersResponse?.containers ?? []).map((name) => ({
      name,
      value: name,
    }));
    if (props.allowUsingSourceContainer) {
      return [{ name: 'Use source containers', value: '' }, ...list];
    }
    return list;
  }, [containersResponse?.containers, props]);

  const setRestoreStorageAccountName = (value?: AzureStorageAccount) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      StorageAccount: {
        ...state.StorageAccount,
        AccountName: value?.name,
        ResourceGroup: value?.resourceGroup,
        Container: '',
      },
    }));
  };

  const setContainer = (value: string) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      StorageAccount: { ...state.StorageAccount, Container: value },
    }));
  };

  const setPrefix = (value: string) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      prefix: value,
    }));
  };

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        <FormControl size='small' className='flex-1'>
          <FormLabel>Storage Account*</FormLabel>
          <Select
            displayEmpty
            renderValue={(value: string) => {
              if (!value) {
                return <SelectPlaceholder />;
              }
              const storageAccount = storageAccounts.find(
                (x) => x.name === value
              );
              if (!storageAccount) {
                return <SelectPlaceholder />;
              }
              return <StorageAccountRegionItem {...storageAccount} />;
            }}
            className='h-[58px]'
            size='small'
            value={
              storageAccounts.find((x) => x.name === StorageAccount.AccountName)
                ?.name || ''
            }
            onChange={(event) =>
              setRestoreStorageAccountName(
                storageAccounts.find((x) => x.name === event.target.value)
              )
            }
          >
            {storageAccounts.map((storageAccount) => (
              <MenuItem key={storageAccount.name} value={storageAccount.name}>
                <StorageAccountRegionItem {...storageAccount} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size='small' className='flex-1'>
          <FormLabel>Container*</FormLabel>
          <Select
            displayEmpty
            disabled={!StorageAccount.AccountName}
            className='h-[58px]'
            size='small'
            value={StorageAccount.Container || ''}
            onChange={(event) => setContainer(event.target.value)}
          >
            {containersList.map((container) => (
              <MenuItem key={container.name} value={container.value}>
                {container.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        <StorageAccountPrefix prefix={prefix} onChange={setPrefix} />
      </Stack>
    </Box>
  );
};

const StorageAccountRegionItem = (props: {
  name: string;
  location: string;
}) => {
  return (
    <Stack direction={'column'} className='py-[4px]'>
      <Typography>{props.name}</Typography>
      <Typography variant='body2' className='mt-[4px]'>
        {props.location}
      </Typography>
    </Stack>
  );
};

const StorageAccountPrefix = (props: {
  prefix?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Prefix (optional)</FormLabel>
      <Stack height='100%' direction='column' justifyContent='space-between'>
        <TransactionalTextField
          initValue={props.prefix || ''}
          onChange={props.onChange}
        />
        <Typography paddingTop='8px' variant='body2'>
          Example: project/wordFiles/
        </Typography>
      </Stack>
    </FormControl>
  );
};
